import type { EventType } from './types/index'

export interface DenominationConfig {
  key: string
  label: string
  weeklyHighlight: string // "this Sabbath" | "this Sunday" | "this weekend"
  suggestedTags: string[]
  potluckTags: string[]
  eventNameSuggestions: Partial<Record<EventType, string[]>>
}

const SDA: DenominationConfig = {
  key: 'seventh-day-adventist',
  label: 'Seventh-day Adventist',
  weeklyHighlight: 'this Sabbath',
  suggestedTags: ['vegetarian', 'all-welcome', 'health-conscious'],
  potluckTags: ['plant-based', 'gluten-free', 'no-meat'],
  eventNameSuggestions: {
    potluck: ['Sabbath Fellowship Lunch', 'Vespers Potluck', 'Pathfinder Fundraiser Lunch'],
    worship: ['Vespers Service', 'Prayer Meeting', 'Evangelistic Series'],
    study: ['Prophecy Study', 'Sabbath School Deep Dive', 'Morning Devotions'],
    outdoors: ['Sabbath Nature Walk', 'Creation Sabbath Hike'],
    social: ['Vespers Social', 'AY Social Night'],
    kids: ['Pathfinder Club', 'VBS'],
    service: ['Community Outreach', 'Health Screening Day'],
  },
}

const BAPTIST: DenominationConfig = {
  key: 'baptist',
  label: 'Baptist',
  weeklyHighlight: 'this Sunday',
  suggestedTags: ['family', 'all-welcome', 'potluck'],
  potluckTags: ['southern', 'comfort-food'],
  eventNameSuggestions: {
    potluck: ['Wednesday Night Supper', 'Sunday Fellowship Dinner', 'VBS Kickoff Cookout'],
    study: ['Life Group', 'Bible Study', 'Sunday School'],
    social: ['Game Night', 'Youth Social'],
    outdoors: ['Nature Outing', 'Camping Trip'],
    kids: ['VBS', 'Youth Group'],
    service: ['Mission Project', 'Community Serve Day'],
  },
}

const METHODIST: DenominationConfig = {
  key: 'methodist',
  label: 'Methodist',
  weeklyHighlight: 'this Sunday',
  suggestedTags: ['all-welcome', 'family-friendly', 'community'],
  potluckTags: ['homemade'],
  eventNameSuggestions: {
    potluck: ['Fellowship Dinner', 'Sunday Potluck'],
    study: ['Small Group', 'Bible Study', 'Disciple Class'],
    service: ['United Methodist Volunteers', 'Mission Trip Prep'],
    social: ['Church Social', 'Advent Party'],
    outdoors: ['Nature Walk', 'Family Hike'],
    kids: ['Youth Group', 'VBS'],
  },
}

const NON_DENOM: DenominationConfig = {
  key: 'non-denominational',
  label: 'Non-denominational',
  weeklyHighlight: 'this weekend',
  suggestedTags: ['all-welcome', 'community', 'family'],
  potluckTags: ['potluck'],
  eventNameSuggestions: {
    potluck: ['Community Potluck', 'Church Lunch', 'Neighborhood Cookout'],
    study: ['Life Group', 'Home Group', 'Bible Study'],
    social: ['Community Social', 'Game Night'],
    outdoors: ['Group Hike', 'Pickup Games'],
    kids: ['Kids Ministry', 'VBS'],
    service: ['Serve Day', 'Community Outreach'],
  },
}

const CATHOLIC: DenominationConfig = {
  key: 'catholic',
  label: 'Catholic',
  weeklyHighlight: 'this weekend',
  suggestedTags: ['parish', 'all-welcome', 'volunteer'],
  potluckTags: ['fish-friendly'],
  eventNameSuggestions: {
    potluck: ['Parish Dinner', 'Fish Fry', "St. Patrick's Potluck"],
    worship: ['Holy Hour', 'Praise & Worship Night'],
    study: ['RCIA Study', 'Knights of Columbus Meeting', 'Theology on Tap'],
    service: ['Soup Kitchen', 'Catholic Charities Volunteer Day'],
    social: ['Youth Ministry Social', 'Parish Social'],
    outdoors: ['Pilgrimage Walk', 'CYO Sports'],
    kids: ['CCD Class', 'Youth Group'],
  },
}

const LUTHERAN: DenominationConfig = {
  key: 'lutheran',
  label: 'Lutheran',
  weeklyHighlight: 'this Sunday',
  suggestedTags: ['all-welcome', 'community', 'family-friendly'],
  potluckTags: ['homemade'],
  eventNameSuggestions: {
    potluck: ['Church Potluck', 'Lutherfest Dinner'],
    study: ['Bible Study', "Luther's Catechism Class"],
    social: ['Church Social', 'Youth Group Night'],
    outdoors: ['Nature Hike', 'Congregational Games'],
    kids: ['Youth Group', 'Sunday School'],
    service: ['LWR Service Project'],
  },
}

const PRESBYTERIAN: DenominationConfig = {
  key: 'presbyterian',
  label: 'Presbyterian',
  weeklyHighlight: 'this Sunday',
  suggestedTags: ['all-welcome', 'community'],
  potluckTags: ['homemade'],
  eventNameSuggestions: {
    potluck: ['Kirk Night Potluck', 'Sunday Fellowship'],
    study: ['Presbyterian Study', 'Small Group', 'Covenant Group'],
    social: ['Church Social', 'Game Night'],
    outdoors: ['Group Hike', 'Recreational Sports'],
    kids: ['Youth Group', 'VBS'],
    service: ['Mission Committee Serve Day'],
  },
}

export const GENERIC_DENOMINATION_CONFIG: DenominationConfig = {
  key: '',
  label: 'Any denomination',
  weeklyHighlight: 'this weekend',
  suggestedTags: ['all-welcome', 'family-friendly', 'community'],
  potluckTags: ['potluck'],
  eventNameSuggestions: {
    potluck: ['Community Potluck', 'Fellowship Lunch'],
    study: ['Bible Study', 'Small Group'],
    social: ['Social Gathering', 'Game Night'],
    outdoors: ['Group Hike', 'Pickup Games'],
    kids: ['Youth Group', 'VBS'],
    service: ['Volunteer Day', 'Community Service'],
  },
}

const CONFIGS: Record<string, DenominationConfig> = {
  'seventh-day-adventist': SDA,
  'baptist': BAPTIST,
  'methodist': METHODIST,
  'non-denominational': NON_DENOM,
  'catholic': CATHOLIC,
  'lutheran': LUTHERAN,
  'presbyterian': PRESBYTERIAN,
}

export function getDenominationConfig(denomination: string | null | undefined): DenominationConfig {
  return CONFIGS[denomination ?? ''] ?? GENERIC_DENOMINATION_CONFIG
}

export const DENOMINATION_KEYS = Object.keys(CONFIGS) as Array<keyof typeof CONFIGS>

// Denominations with full event configs + additional known denominations from OSM data.
// Sorted alphabetically by label for dropdowns.
export const DENOMINATION_OPTIONS: Array<{ key: string; label: string }> = [
  ...Object.values(CONFIGS).map((c) => ({ key: c.key, label: c.label })),
  { key: 'african-methodist-episcopal', label: 'African Methodist Episcopal' },
  { key: 'church-of-christ', label: 'Church of Christ' },
  { key: 'church-of-god', label: 'Church of God' },
  { key: 'church-of-the-nazarene', label: 'Church of the Nazarene' },
  { key: 'episcopal', label: 'Episcopal' },
  { key: 'evangelical', label: 'Evangelical' },
  { key: 'latter-day-saints', label: 'Latter-day Saints' },
  { key: 'orthodox', label: 'Orthodox' },
  { key: 'pentecostal', label: 'Pentecostal' },
].sort((a, b) => a.label.localeCompare(b.label))

const DENOMINATION_ALIASES: Record<string, string> = {
  // OSM raw tag values
  seventh_day_adventist: 'seventh-day-adventist',
  adventist: 'seventh-day-adventist',
  sda: 'seventh-day-adventist',
  roman_catholic: 'catholic',
  'roman-catholic': 'catholic',
  roman: 'catholic',
  united_methodist: 'methodist',
  'united-methodist': 'methodist',
  non_denominational: 'non-denominational',
  nondenominational: 'non-denominational',
  greek_orthodox: 'orthodox',
  'greek-orthodox': 'orthodox',
  african_methodist_episcopal: 'african-methodist-episcopal',
  african_methodist_episcopal_zion: 'african-methodist-episcopal',
  southern_baptist: 'baptist',
  presbyterian_church_usa: 'presbyterian',
  cumberland_presbyterian_church: 'presbyterian',
  latter_day_saints: 'latter-day-saints',
  lds: 'latter-day-saints',
  mormon: 'latter-day-saints',
  church_of_christ: 'church-of-christ',
  church_of_god: 'church-of-god',
  nazarene: 'church-of-the-nazarene',
  anglican: 'episcopal',
  // Label forms (returned by Claude or user input)
  'seventh-day adventist': 'seventh-day-adventist',
  'seventh day adventist': 'seventh-day-adventist',
  'roman catholic': 'catholic',
  'united methodist': 'methodist',
  'non-denominational': 'non-denominational',
  'non denominational': 'non-denominational',
  'church of christ': 'church-of-christ',
  'church of god': 'church-of-god',
  'church of the nazarene': 'church-of-the-nazarene',
  'latter-day saints': 'latter-day-saints',
  'latter day saints': 'latter-day-saints',
  'african methodist episcopal': 'african-methodist-episcopal',
  'greek orthodox': 'orthodox',
  'assembly of god': 'pentecostal',
  'assemblies of god': 'pentecostal',
  foursquare: 'pentecostal',
}

const ALL_KNOWN_KEYS = new Set(DENOMINATION_OPTIONS.map((d) => d.key))

export function normalizeDenomination(raw: string | null | undefined): string | null {
  if (!raw) return null
  const lower = raw.toLowerCase().trim()
  if (DENOMINATION_ALIASES[lower]) return DENOMINATION_ALIASES[lower]
  if (ALL_KNOWN_KEYS.has(lower)) return lower
  // Convert underscores/spaces → hyphens and check again
  const kebab = lower.replace(/[\s_]+/g, '-')
  if (DENOMINATION_ALIASES[kebab]) return DENOMINATION_ALIASES[kebab]
  if (ALL_KNOWN_KEYS.has(kebab)) return kebab
  // Unknown — preserve normalized form rather than drop it
  return kebab
}
